/**
 * Resolves artist slug for OneSignal External ID (e.g. "qastar").
 */

import type { User } from '@/api/types';
import type { ArtistDetailsData } from '@/types/artistDetails';
import { storageManager } from '@/storage/storageManager';
import { STORAGE_KEYS } from '@/storage/storageKeys';
import { getAuthCookies } from '@/utils/cookies';

/** True for non-empty slug strings; rejects pure positive numbers (legacy numeric ids). */
export function isValidArtistSlug(value: unknown): boolean {
  if (value == null) return false;
  const s = String(value).trim();
  if (!s) return false;
  const n = Number(s);
  if (Number.isFinite(n) && n > 0 && String(Math.trunc(n)) === s) return false;
  return true;
}

function slugFromUser(user: User | null | undefined): string | null {
  if (!user?.id) return null;
  const slug = user.id.trim();
  return isValidArtistSlug(slug) ? slug : null;
}

function slugFromDetails(details: ArtistDetailsData | null | undefined): string | null {
  const slug = details?.user_info?.artist_slug?.trim();
  return slug && isValidArtistSlug(slug) ? slug : null;
}

function slugFromCookie(): string | null {
  const slug = getAuthCookies().active_artist_slug?.trim();
  return slug && isValidArtistSlug(slug) ? slug : null;
}

/** Sync slug resolution (user → details → cookie). */
export function resolveArtistSlugSync(
  details?: ArtistDetailsData | null,
  user?: User | null,
): string | null {
  return slugFromUser(user) ?? slugFromDetails(details) ?? slugFromCookie();
}

/**
 * Full slug resolution including persisted userData when auth store is not hydrated yet.
 */
export async function resolveArtistSlug(
  details?: ArtistDetailsData | null,
  user?: User | null,
): Promise<string | null> {
  const fromUser = slugFromUser(user);
  if (fromUser) return fromUser;

  const stored = await storageManager.get<User>(STORAGE_KEYS.USER_DATA);
  const fromStored = slugFromUser(stored);
  if (fromStored) return fromStored;

  return slugFromDetails(details) ?? slugFromCookie();
}
